import { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/common/DataTable';
import { AllBetEntry } from '@/types';
import { dateSortingFn } from '@/utils/date-sort';
import { useEffect, useState } from 'react';
import { apiClient } from '@/utils/api';

export default function AllBetsPage() {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState<AllBetEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // ✅ Fetch bets dynamically
  const fetchBets = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/reports/bets', {
        params: {
          skip: pageIndex * pageSize,
          take: pageSize,
        },
      });

      setTotal(res.data.count);

      setData(
        res.data.data.map((bet: any) => ({
          betId: bet.id.toString(),
          playerId: bet.userId.toString(),
          roundId: bet.roundId,
          betType: bet.betType,
          betAmount: Number(bet.amount),
          payout: Number(bet.payout ?? 0),
          status: bet.status === 'Won' ? 'Win' : 'Lose',
          timestamp: bet.createdAt,
        }))
      );
    } finally {
      setLoading(false);
    }
  };

  // 🔁 Fetch on pagination change
  useEffect(() => {
    fetchBets();
  }, [pageIndex, pageSize]);

  // TABLE COLUMNS
  const allBetColumns: ColumnDef<AllBetEntry>[] = [
    {
      header: 'Sr.',
      cell: ({ row }) => pageIndex * pageSize + row.index + 1,
    },
    { accessorKey: 'betId', header: 'Bet ID' },
    { accessorKey: 'playerId', header: 'Player ID' },
    { accessorKey: 'roundId', header: 'Round ID' },
    {
      accessorKey: 'betType',
      header: 'Bet Type',
      cell: ({ row }) => {
        const type = row.original.betType;
        const color =
          type === 'Dragon'
            ? 'text-red-400'
            : type === 'Tiger'
              ? 'text-blue-400'
              : type === 'Tie'
                ? 'text-yellow-400'
                : 'text-purple-400';

        return <span className={`font-semibold ${color}`}>{type}</span>;
      },
    },
    {
      accessorKey: 'betAmount',
      header: 'Bet Amount',
      cell: ({ row }) => `₹${row.original.betAmount.toLocaleString()}`,
    },
    {
      accessorKey: 'payout',
      header: 'Payout',
      cell: ({ row }) => `₹${row.original.payout.toLocaleString()}`,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const color = row.original.status === 'Win' ? 'text-green-500' : 'text-red-500';
        return <span className={`font-semibold ${color}`}>{row.original.status}</span>;
      },
    },
    {
      accessorKey: 'timestamp',
      header: 'Time',
      sortingFn: dateSortingFn,
      cell: ({ row }) =>
        new Date(row.original.timestamp).toLocaleString('en-IN', {
          dateStyle: 'medium',
          timeStyle: 'short',
        }),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="mb-10">
        <h2 className="text-3xl font-bold tracking-tight">All Player Bets</h2>
        <p className="text-muted-foreground">
          Comprehensive list of all bets placed across all rounds.
        </p>
      </div>

      <DataTable
        columns={allBetColumns}
        data={data}
        enableSelection
        enableExport
        enableFilters
        pageIndex={pageIndex}
        pageSize={pageSize}
        total={total}
        onPageChange={setPageIndex}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPageIndex(0);
        }}
      />
    </div>
  );
}
